import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import SEOHead from '@/components/common/SEOHead';
import WCSubmenu from '@/components/worldcup/WCSubmenu';
import { LoadingPage } from '@/components/common/Loading';
import { Users, ChevronRight, Trophy } from 'lucide-react';
import { translateGroupName, translateCountryName } from '@/lib/wcTranslations';

const API_BASE = '';

export default function WorldCupGroupsPage() {
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const wcBase = currentLang === 'tr' ? `/${currentLang}/dunya-kupasi-2026` : `/${currentLang}/world-cup-2026`;

  useEffect(() => {
    fetch(`${API_BASE}/api/worldcup/hub?lang=${currentLang}`)
      .then(r => r.json()).then(d => {
        setGroups((d.groups || []).filter(g => !g.name.includes('Ranking')));
        setLoading(false);
      }).catch(() => setLoading(false));
  }, [currentLang]);

  if (loading) return <LoadingPage />;

  return (
    <div data-testid="wc-groups-page">
      <WCSubmenu />
      <SEOHead title={t('wc.groupsLabel') + ' - ' + t('wc.breadcrumb') + ' | ODDSIS'}
        canonical={`https://oddsis.com/${currentLang}/${currentLang === 'tr' ? 'dunya-kupasi-2026/gruplar' : 'world-cup-2026/groups'}`} />

      {/* Hero */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #020617 0%, #0a1628 40%, #1a2744 100%)' }}>
        <div className="relative z-10 container mx-auto px-4 max-w-7xl py-8">
          <div className="flex items-center gap-2 text-sm text-slate-400 mb-4">
            <Link to={`/${currentLang}`} className="hover:text-slate-200">{t('nav.home')}</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to={wcBase} className="hover:text-amber-300 text-amber-400/70">{t('wc.breadcrumb')}</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white">{t('wc.groupsLabel')}</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Users className="w-8 h-8 inline mr-3 text-amber-400" />
            {t('wc.allGroups')}
          </h1>
          <p className="text-sm text-slate-400">12 {t('wc.groups').toLowerCase()} - 48 {t('wc.teams').toLowerCase()}</p>
        </div>
      </div>

      <div className="container mx-auto px-4 max-w-7xl py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {groups.map(group => {
            const nameMatch = group.name.match(/Group\s*([A-L])/i);
            const letter = nameMatch ? nameMatch[1].toLowerCase() : group.name.replace(/[^a-zA-Z]/g, '').toLowerCase();
            const path = currentLang === 'tr' ? `${wcBase}/grup/${letter}` : `${wcBase}/group/${letter}`;
            const displayName = currentLang === 'tr' ? `Grup ${letter.toUpperCase()}` : `Group ${letter.toUpperCase()}`;
            const isTurkeyGroup = group.teams?.some(t => t.team_name === 'Türkiye');
            return (
              <Link key={group.name} to={path} data-testid={`group-card-${letter}`}
                className={`group bg-slate-900/50 backdrop-blur-md border rounded-2xl p-5 transition-all duration-300 hover:border-amber-500/50 hover:bg-slate-800/80 hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(245,158,11,0.1)] ${
                  isTurkeyGroup ? 'border-red-500/30 bg-red-950/20' : 'border-slate-800'
                }`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-base font-black ${isTurkeyGroup ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/10 text-amber-400'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {letter.toUpperCase()}
                    </div>
                    <h3 className="text-sm font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{displayName}</h3>
                  </div>
                  {isTurkeyGroup && <span className="text-[9px] bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full font-bold">TR</span>}
                  <ChevronRight className="w-4 h-4 text-slate-700 group-hover:text-amber-400 transition-colors" />
                </div>
                <div className="space-y-2">
                  {group.teams?.slice(0, 4).map((team, i) => (
                    <div key={i} className="flex items-center gap-2.5">
                      <span className="w-4 text-right text-[10px] text-slate-600 font-mono">{i + 1}</span>
                      <img src={team.team_logo} alt="" className="w-5 h-5 rounded-sm" />
                      <span className={`text-xs font-medium ${team.team_name === 'Türkiye' ? 'text-red-400 font-bold' : 'text-slate-300'}`}>{translateCountryName(team.team_name, currentLang)}</span>
                      <span className="ml-auto text-[10px] text-slate-600 tabular-nums">{team.points}p</span>
                    </div>
                  ))}
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
